// Minimal Cognito OIDC auth for the admin portal. Mirrors the shape of the
// main app's AuthProvider but is self-contained — the admin portal is a
// separate deploy and we don't want to couple its build graph to the main
// frontend's Sentry, BroadcastChannel, and MFA plumbing.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { setTokenProvider } from './api.js'
import { setTokenProvider as setRestTokenProvider } from '@api/client'

const region = import.meta.env.VITE_COGNITO_REGION || 'eu-west-1'
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || ''
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || ''
const authDomain = import.meta.env.VITE_COGNITO_AUTH_DOMAIN || ''
const appUrl = import.meta.env.VITE_APP_URL || window.location.origin

let userManager = null
if (userPoolId && clientId && authDomain) {
  userManager = new UserManager({
    authority: `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`,
    client_id: clientId,
    redirect_uri: `${appUrl}/callback`,
    post_logout_redirect_uri: appUrl,
    response_type: 'code',
    scope: 'openid email profile aws.cognito.signin.user.admin',
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    metadata: {
      authorization_endpoint: `https://${authDomain}/oauth2/authorize`,
      token_endpoint: `https://${authDomain}/oauth2/token`,
      userinfo_endpoint: `https://${authDomain}/oauth2/userInfo`,
      end_session_endpoint: `https://${authDomain}/logout`,
    },
  })
}

const AuthContext = createContext(null)
export const useAuth = () => useContext(AuthContext)

function parseUser(u) {
  if (!u) return null
  return {
    sub: u.profile.sub,
    email: u.profile.email,
    name: u.profile.name || u.profile.email,
    idToken: u.id_token,
    accessToken: u.access_token,
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const getToken = useCallback(async () => {
    if (!userManager) return null
    const u = await userManager.getUser()
    if (u && !u.expired) return u.id_token
    if (u && u.expired) {
      const renewed = await userManager.signinSilent().catch(() => null)
      if (renewed && !renewed.expired) return renewed.id_token
    }
    return null
  }, [])

  // REST client (@api/client) expects a raw access token; it wraps in "Bearer".
  const getAccessToken = useCallback(async () => {
    if (!userManager) return null
    const u = await userManager.getUser()
    if (u && !u.expired) return u.access_token
    if (u && u.expired) {
      const renewed = await userManager.signinSilent().catch(() => null)
      if (renewed && !renewed.expired) return renewed.access_token
    }
    return null
  }, [])

  useEffect(() => {
    setTokenProvider(getToken)
    setRestTokenProvider(getAccessToken)
  }, [getToken, getAccessToken])

  useEffect(() => {
    if (!userManager) { setLoading(false); return }
    userManager.getUser().then((u) => {
      if (u && !u.expired) setUser(parseUser(u))
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const signIn = useCallback(() => userManager?.signinRedirect(), [])
  const signOut = useCallback(async () => {
    await userManager?.removeUser()
    setUser(null)
    window.location.href = `https://${authDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(appUrl)}`
  }, [])

  const handleCallback = useCallback(async () => {
    const u = await userManager.signinRedirectCallback()
    setUser(parseUser(u))
    return u
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, handleCallback }}>
      {children}
    </AuthContext.Provider>
  )
}
